/**
 * How a park is titled in the UI.
 *
 * THE NAMES DO NOT EXIST, and that is not a gap we can close. Verified against primary sources
 * before writing this, rather than assumed:
 *
 *   - Madrid's OWN municipal dataset (datos.madrid.es, "Áreas caninas", CC BY 4.0) holds 153
 *     records — exactly the 153 we have for Madrid — across TWENTY-THREE columns, and not one of
 *     them is a name. The city identifies a dog area by TIPO_VIA + NOM_VIA + NUM_VIA (street),
 *     BARRIO and DISTRITO.
 *   - Barcelona's equivalent: 93 records, no name field either, but an `espai_verd` column holding
 *     the CONTAINING PARK ("Poblenou, Parc").
 *   - Of the 10 Madrid parks that DO carry an OSM `name`, three are private businesses and the rest
 *     are the generic word "Pipican" or a literal municipal asset code ("Área canina C2075" — that
 *     string IS Madrid's CODIGO_INTERNO). A mapper reached for an internal asset code because there
 *     was no name to reach for.
 *
 * So a Spanish municipal dog area genuinely has no name, and printing "Zona canina" 153 times was
 * an honest but useless answer. The fix is to identify it the way both city governments do: BY
 * WHERE IT IS. `place_label` is derived at build time (scripts/spain/enrich_place_labels.mjs) from
 * the containing park, or failing that the nearest street:
 *
 *     Zona canina · Parque de Roma
 *     Zona canina · Calle de Antonio Machado
 *
 * Priority is Barcelona's and Madrid's own: containing green space first, then street.
 *
 * Do NOT invent names, and do not fall back to a bearing-and-distance ("southeast, 0.9km from the
 * centre") — no source identifies a park that way, and a visitor cannot use it to find anything.
 */

// The CATEGORY translates; the PLACE never does. "Parque de Roma" is a proper noun, and an English
// visitor standing in Madrid needs the name that is written on the sign, not a translation of it.
const FALLBACK = { es: 'Zona canina', en: 'Dog area' }
const fb = (locale) => FALLBACK[locale] || FALLBACK.es

/** True when OSM actually gave this park a name of its own. */
export function hasRealName(park) {
  const n = park?.name?.trim()
  if (!n) return false
  // "Pipican" is the generic Spanish word for a dog toilet area, and "Área canina C2075" is a
  // municipal asset code. Neither is a name; both came from mappers with nothing better to write.
  if (/^pipi?can$/i.test(n)) return false
  if (/^[ÁA]rea canina C\d+$/i.test(n)) return false
  if (/^(zona|[áa]rea|parque)\s+(canina?|de recreo canina?|de entrenamiento canino)$/i.test(n)) return false
  return true
}

/**
 * IN-CONTEXT title: park cards, map pins, the nearby-parks list.
 *
 * NO category prefix. The reader is on a dog-park directory, on a city page, under a heading that
 * says "Los 153 parques" — telling them 153 times that each one is a "Zona canina" is noise, and it
 * shoves the only thing that actually distinguishes the row (the street) to the right. The category
 * is redundant exactly where the context already supplies it.
 *
 * The prefix survives ONLY where the park travels without its context — see parkTitleSeo().
 */
export function parkTitle(park, locale = 'es') {
  if (hasRealName(park)) return park.name.trim()
  const place = park?.place_label?.trim()
  // No place either (37 parks): the category is all we have, so it IS the title.
  return place || fb(locale)
}

/**
 * OUT-OF-CONTEXT title: the park page's <title>, its meta description, its JSON-LD `name`.
 *
 * KEEPS the category, and this is the one place it earns its keep. These strings appear in a Google
 * result, a shared link, an answer-engine citation — stripped of every bit of surrounding context.
 * "Avenida de las Comunidades, Madrid" reads like a street address; nothing in it says the page is
 * about a dog park, and it is the only line the searcher gets. So the category stays here, and
 * nowhere else.
 */
export function parkTitleSeo(park, locale = 'es') {
  if (hasRealName(park)) return park.name.trim()
  const place = park?.place_label?.trim()
  return place ? `${fb(locale)} · ${place}` : fb(locale)
}

/** Just the place part, for a subtitle line where the category is already shown. */
export function parkPlace(park) {
  return park?.place_label?.trim() || null
}

/** Title for a park out of context (search results, meta tags, structured data). */
export function parkTitleWithCity(park, locale = 'es') {
  const city = park?.city_name
  if (hasRealName(park)) {
    const n = park.name.trim()
    return city ? `${n}, ${city}` : n
  }
  const place = park?.place_label?.trim()
  if (place && city) return `${fb(locale)} · ${place}, ${city}`
  if (place) return `${fb(locale)} · ${place}`
  if (!city) return fb(locale)
  return locale === 'en' ? `${fb(locale)} in ${city}` : `${fb(locale)} en ${city}`
}

/**
 * Two dog areas inside the same park share a label, so a list can read "Parque Darwin" twice.
 * Madrid genuinely has two in Parque Darwin — the duplicate is the data being honest, not wrong.
 * Number them in document order so they are at least distinguishable and stable.
 */
export function disambiguate(parks, locale = 'es') {
  const counts = new Map()
  for (const p of parks) {
    const t = parkTitle(p, locale)
    counts.set(t, (counts.get(t) || 0) + 1)
  }
  const seen = new Map()
  return parks.map((p) => {
    const t = parkTitle(p, locale)
    if ((counts.get(t) || 0) < 2) return { ...p, _title: t }
    const i = (seen.get(t) || 0) + 1
    seen.set(t, i)
    return { ...p, _title: `${t} (${i})` }
  })
}
